import React, { useState, useEffect } from "react";
import { Table, Button, Space, notification, Typography } from "antd";
import { SyncOutlined } from "@ant-design/icons";
import moment from "moment";
import PageWrapper from "../../../ui/PageWrapper";
import { EpvoSyncNameService } from "./EpvoSyncNameService";
import { errorNotification } from "../../../helpers/errorNotification";

interface EpvoSyncRecord {
    id: number;
    codeName: string;
    description: string;
    lastUpdateAt: string | null;
}

interface EpvoSyncHistoryRecord {
    id: number;
    name: EpvoSyncRecord;
    isSync: boolean;
    createdAt: string;
}

const EpvoSyncNamePage: React.FC = () => {
    const [data, setData] = useState<EpvoSyncRecord[]>([]);
    const [loading, setLoading] = useState(false);
    const [history, setHistory] = useState<EpvoSyncHistoryRecord[]>([]);

    useEffect(() => {
        fetchEpvoSyncNames();
        fetchEpvoSyncHistory();
    }, []);

    const fetchEpvoSyncNames = async () => {
        setLoading(true);
        try {
            const response = await EpvoSyncNameService.getAllEpvoSyncName();
            setData(response.data);
        } catch (error) {
            notification.error({ message: "Failed to fetch EPVO sync names" });
        } finally {
            setLoading(false);
        }
    };

    const fetchEpvoSyncHistory = async () => {
        setLoading(true);
        try {
            const response = await EpvoSyncNameService.getAllEpvoSyncHistory();
            setHistory(response.data);
        } catch (error) {
            errorNotification("Failed to fetch EPVO sync history");
        } finally {
            setLoading(false);
        }
    };

    const handleSyncAll = async () => {
        setLoading(true);
        try {
            await EpvoSyncNameService.sync();
            notification.success({ message: "Sync completed successfully" });
            fetchEpvoSyncNames();
        } catch (error) {
            notification.error({ message: "Failed to sync" });
        } finally {
            setLoading(false);
        }
    };

    const handleSyncById = async (id: number) => {
        setLoading(true);
        try {
            await EpvoSyncNameService.syncByCodeNameId(id);
            notification.success({ message: `Sync completed` });
            fetchEpvoSyncNames();
        } catch (error) {
            notification.error({ message: `Failed to sync` });
        } finally {
            setLoading(false);
        }
    };

    const columns = [
        {
            title: "Code Name",
            dataIndex: "codeName",
            key: "codeName",
        },
        {
            title: "Description",
            dataIndex: "description",
            key: "description",
        },
        {
            title: "Last Update At",
            dataIndex: "lastUpdateAt",
            key: "lastUpdateAt",
            render: (text: string | null) => (text ? moment(text).format("HH:mm:ss DD.MM.YYYY") : "-"),
        },
        {
            title: "Actions",
            key: "actions",
            render: (_: unknown, record: EpvoSyncRecord) => (
                <Button
                    type="primary"
                    icon={<SyncOutlined />}
                    onClick={() => handleSyncById(record.id)}
                    loading={loading}
                >
                    Sync
                </Button>
            ),
        },
    ];

    const historyColumns = [
        // {
        //     title: "ID",
        //     dataIndex: "id",
        //     key: "id",
        // },
        {
            title: "Code Name",
            dataIndex: ["name", "codeName"],
            key: "codeName",
        },
        {
            title: "Synced",
            dataIndex: "isSync",
            key: "isSync",
            render: (isSync: boolean) => (isSync ? "Yes" : "No"),
        },
        {
            title: "Created At",
            dataIndex: "createdAt",
            key: "createdAt",
            render: (text: string) => moment(text).format("HH:mm:ss DD.MM.YYYY"),
        },
    ];

    return (
        <PageWrapper>
            <Typography.Title level={1}>Epvo Sync</Typography.Title>
            <Space style={{ marginBottom: 16 }}>
                <Button type="primary" icon={<SyncOutlined />} onClick={handleSyncAll} loading={loading}>
                    Sync All
                </Button>
            </Space>
            <Table columns={columns} dataSource={data} rowKey="id" loading={loading} />

            <Typography.Title level={2} style={{ marginTop: 32 }}>Sync History</Typography.Title>
            <Table columns={historyColumns} dataSource={history} rowKey="id" loading={loading} />
        </PageWrapper>
    );
};

export default EpvoSyncNamePage;
